import { API_URL } from "../../config";
import type {
  ImportResult,
  PreviewResponse,
  StaffListResponse,
  SummaryResponse,
} from "./types";

// The existing app uses RTK Query, but this admin tool is a small self-contained
// surface — a plain fetch client keeps its network layer independent so any
// future changes here don't ripple through the shared api slice. Auth token is
// read from the same localStorage key RTK Query already uses.
async function post<T>(path: string, body: Record<string, unknown>): Promise<T> {
  // API_URL already ends in `/api` (matches attoDeskApi baseUrl), so paths
  // here are relative to it — do NOT prepend `/api` again.
  const res = await fetch(`${API_URL}/class-backup-import${path}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
    },
    body: JSON.stringify(body),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err: any = new Error(json?.error || json?.message || `HTTP ${res.status}`);
    err.status = res.status;
    err.body = json;
    throw err;
  }
  return json as T;
}

export const classBackupApi = {
  summary: (cutoff_date: string) =>
    post<SummaryResponse>("/summary", { cutoff_date }),

  staffList: (params: {
    cutoff_date: string;
    search?: string;
    status?: string;
    has_future?: boolean;
    // §8 — Class Roster filter.
    class_roster?: "all" | "non_empty" | "empty_only" | "none";
    page?: number;
    per_page?: number;
  }) => post<StaffListResponse>("/staff-list", params),

  preview: (legacy_staff_id: number, cutoff_date: string) =>
    post<PreviewResponse>("/preview", { legacy_staff_id, cutoff_date }),

  startRun: (params: {
    cutoff_date: string;
    selection_mode: "single" | "multi" | "all-active";
    mode: "validate" | "import";
    total_staff: number;
  }) => post<{ run_id: number }>("/run/start", params),

  finalizeRun: (run_id: number) =>
    post<{ ok: true }>("/run/finalize", { run_id }),

  validateStaff: (run_id: number, legacy_staff_id: number, cutoff_date: string) =>
    post<ImportResult>("/staff/validate", { run_id, legacy_staff_id, cutoff_date }),

  importStaff: (run_id: number, legacy_staff_id: number, cutoff_date: string) =>
    post<ImportResult>("/staff/import", { run_id, legacy_staff_id, cutoff_date }),

  // §6 "Update All Classes" — batch update for selected legacy staff ids
  // that already have ≥1 mapped live class. Backend skips first-time creates.
  updateAllClasses: (run_id: number, legacy_staff_ids: number[], cutoff_date: string) =>
    post<{ items: any[] }>("/update-all", { run_id, legacy_staff_ids, cutoff_date }),

  mapStaff: (legacy_staff_id: number, live_staff_id: number, force = false) =>
    post<{ ok: true } | { error: string; requires_confirmation?: boolean; existing_live_staff_id?: number }>(
      "/map-staff",
      { legacy_staff_id, live_staff_id, force }
    ),

  liveStaffSearch: (q: string) =>
    post<{
      items: Array<{
        id: number;
        teamId: string | null;
        teamFirstName: string | null;
        teamLastName: string | null;
        teamEmail: string | null;
        legacy_staff_id: number | null;
      }>;
    }>("/live-staff-search", { q }),

  listRuns: () => post<{ items: any[] }>("/runs", {}),
  runLogs: (run_id: number) => post<{ items: any[] }>("/run/logs", { run_id }),
};
